import { useState, useEffect, memo, useCallback, useMemo } from 'react';
import GenericFileDownload from '../GenericFileDownload';

const DocumentIframe = memo(({ url, onLoad, onError }) => {
  const handleError = (err) => {
    console.error('Iframe loading error:', err);
    onError?.(err);
  };
  return (
    <>
      <iframe
        key={url}
        src={url}
        className="file-viewer"
        style={{ width: '100%', height: 'calc(100vh - 200px)', minHeight: '700px' }}
        frameBorder="0"
        onLoad={onLoad}
        onError={handleError}
      >
        This is an embedded{' '}
        <a target="_blank" rel="noopener noreferrer" href="http://office.com">
          Microsoft Office
        </a>{' '}
        document, powered by{' '}
        <a target="_blank" rel="noopener noreferrer" href="http://office.com/webapps">
          Office Online
        </a>
        .
      </iframe>
    </>
  );
});

const GenericDocxViewer = memo(({ fileUrl, onLoad, item }) => {
  const [iFrameUrl, setIframeUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Validate document URL
  useEffect(() => {
    if (fileUrl) {
      try {
        new URL(fileUrl);
      } catch (e) {
        console.error('Invalid document URL:', e);
        setLoadError('Invalid document URL');
        setIsLoading(false);
      }
    }
  }, [fileUrl]);

  // Add timeout for loading
  useEffect(() => {
    let timeoutId;
    if (isLoading) {
      timeoutId = setTimeout(() => {
        handleRetry();
        setLoadError('Loading timeout - document might be too large or unavailable');
        setIsLoading(false);
      }, 10000); // 10 second timeout
    }
    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  // Update URL construction
  useEffect(() => {
    if (fileUrl) {
      try {
        setIsLoading(true);
        setLoadError(null);
        const encodedUrl = encodeURIComponent(fileUrl);
        const newUrl = `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true&t=${Date.now()}`;
        setIframeUrl(newUrl);
      } catch (error) {
        console.error('Error constructing URL:', error);
        setLoadError('Failed to load document');
        setIsLoading(false);
      }
    }
  }, [fileUrl]);

  const handleRetry = useCallback(() => {
    if (retryCount < 3) {
      setIsLoading(true);
      setLoadError(null);
      setRetryCount((prev) => prev + 1);
      const timestamp = new Date().getTime();
      setIframeUrl((current) => `${current}${current.includes('?') ? '&' : '?'}t=${timestamp}`);
    }
  }, [retryCount]);

  const handleIframeLoad = useMemo(() => {
    return () => {
      setIsLoading(false);
      setLoadError(null);
      setRetryCount(0);
      onLoad?.();
      console.log('Iframe loaded');
    };
  }, [onLoad]);

  const handleIframeError = useMemo(() => {
    return (error) => {
      console.error('Iframe loading error:', error);
      setIsLoading(false);
      setLoadError('Failed to load document');
      handleRetry();
    };
  }, [handleRetry]);

  const handleManualRetry = useCallback(() => {
    setRetryCount(0);
    handleRetry();
  }, [handleRetry]);

  if (!fileUrl) return null;

  return (
    <div className="h-100 w-100 d-flex flex-column">
      {item && <GenericFileDownload file={item} iconClassName="icon-save p-2 pointer float-end h2 mb-0" />}
      {loadError && retryCount >= 3 && (
        <div className="alert alert-danger">
          {loadError}
          <button className="btn btn-link" onClick={handleManualRetry}>
            Try again
          </button>
        </div>
      )}
      {iFrameUrl && !loadError && (
        <div className="flex-grow-1 w-100">
          <DocumentIframe url={iFrameUrl} onLoad={handleIframeLoad} onError={handleIframeError} />
        </div>
      )}
    </div>
  );
});

export default GenericDocxViewer;
